// Reference-data hooks. These lookup tables rarely change, so they're cached
// for the whole session.
import { useQuery } from "@tanstack/react-query";
import { getBrands, getPlatforms, getMetricCatalog, getBenchmarks, getDataSources, getMarkets, getFxRates, getAvailability, type Availability } from "@/lib/api/reference";
import { fxMap } from "@/lib/currency";
import type { Brand, Platform, MetricDef, Benchmark, DataSource, Market, FxRate } from "@/lib/api/types";

const REF = { staleTime: Infinity, gcTime: Infinity } as const;

export function useReference() {
  const brands = useQuery({ queryKey: ["ref", "brands"], queryFn: getBrands, ...REF });
  const platforms = useQuery({ queryKey: ["ref", "platforms"], queryFn: getPlatforms, ...REF });
  const metrics = useQuery({ queryKey: ["ref", "metrics"], queryFn: getMetricCatalog, ...REF });
  const benchmarks = useQuery({ queryKey: ["ref", "benchmarks"], queryFn: getBenchmarks, ...REF });
  const markets = useQuery({ queryKey: ["ref", "markets"], queryFn: getMarkets, ...REF });
  const fxRates = useQuery({ queryKey: ["ref", "fx"], queryFn: getFxRates, ...REF });
  const dataSources = useQuery({ queryKey: ["ref", "sources"], queryFn: getDataSources, ...REF });
  // Availability changes as data syncs, so cache it briefly rather than forever.
  const availability = useQuery({ queryKey: ["ref", "availability"], queryFn: getAvailability, staleTime: 5 * 60_000 });

  return {
    brands: (brands.data ?? []) as Brand[],
    platforms: (platforms.data ?? []) as Platform[],
    metrics: (metrics.data ?? []) as MetricDef[],
    benchmarks: (benchmarks.data ?? []) as Benchmark[],
    markets: (markets.data ?? []) as Market[],
    fxRates: (fxRates.data ?? []) as FxRate[],
    /** Display-currency rates, keyed by code — the only source of truth
     *  for converting the rollup's EUR figures for display. */
    fx: fxMap((fxRates.data ?? []) as FxRate[]),
    dataSources: (dataSources.data ?? []) as DataSource[],
    availability: (availability.data ?? []) as Availability[],
    // True while ANY reference query is still on its initial load — gate loading
    // states on this rather than on `data.length === 0` (which can't tell an
    // empty result from a pending one, and flashes empty states on cold loads).
    isLoading: brands.isPending || platforms.isPending || metrics.isPending
      || benchmarks.isPending || markets.isPending || fxRates.isPending || dataSources.isPending,
  };
}

/** Quick lookup helpers built from reference lists. */
export function brandById(brands: Brand[], id: string | null): Brand | undefined {
  return id ? brands.find((b) => b.id === id) : undefined;
}
export function platformById(platforms: Platform[], id: string | null): Platform | undefined {
  return id ? platforms.find((p) => p.id === id) : undefined;
}

/** Set of platform *keys* that are real media channels (excludes tools like
 *  GA4). Used to keep tools out of per-platform breakdowns. */
export function channelKeySet(platforms: Platform[]): Set<string> {
  return new Set(platforms.filter((p) => p.kind === "channel").map((p) => p.key));
}

/** Filter a per-platform breakdown (rows keyed by platform key) to channels only. */
export function channelRows<T extends { key: string }>(rows: T[], platforms: Platform[]): T[] {
  const keys = channelKeySet(platforms);
  return rows.filter((r) => keys.has(r.key));
}
